import axios from 'axios';
import { AsyncAction } from '..';
import { Report } from '../../types/report';

type State = {
    fetching: boolean;
    query?: string;
    sort?: { key: string; order?: 'ascending' | 'descending' };
    reports: Report[];
    error?: Error;
};

type Action = {
    type: 'report_list/get';
    payload?: {
        query?: string;
        sort?: { key: string; order?: 'ascending' | 'descending' };
        reports: Report[];
    };
    error?: Error;
};

const initialState: State = {
    fetching: false,
    reports: [],
};

export const reducer = (state = initialState, action: Action): State => {
    switch (action.type) {
        case 'report_list/get': {
            if (action.payload)
                return {
                    ...state,
                    fetching: false,
                    reports: action.payload.reports,
                    query: action.payload.query,
                    sort: action.payload.sort,
                };
            if (action.error) return { ...state, fetching: false, error: action.error };
            return { ...state, fetching: true };
        }
        default:
            return state;
    }
};

/**
 * Send a request to the back-end to list or search reports that have matching title or timeframe
 *
 * @param params.query query string
 * @param params.sort sort column configuration
 * @param rethrow throw the internal error, causing the promise generated by the dispatch to fail
 */
const get = (
    params: {
        query?: string;
        sort?: { key: string; order?: 'ascending' | 'descending' };
    } = {},
    rethrow = false,
): AsyncAction => async (dispatch, getState) => {
    const { report_list } = getState();
    params.query = params.query ?? report_list.query;
    params.sort = params.sort ?? report_list.sort;
    try {
        dispatch({ type: 'report_list/get' });
        const cancel = axios.CancelToken.source();
        dispatch({ type: 'cancel/set', payload: { 'report-list/get': cancel } });
        const sortParams = params.sort
            ? { ordering: `${params.sort.order === 'ascending' ? '' : '-'}${params.sort.key}` }
            : {};
        const response = await axios.get<Report[]>('/reports/', {
            params: {
                search: params.query,
                ...sortParams,
            },
        });
        dispatch({
            type: 'report_list/get',
            payload: {
                query: params.query,
                sort: params.sort,
                reports: response.data,
            },
        });
    } catch (error) {
        if (error.name === 'AbortError') return;
        dispatch({ type: 'report_list/get', error });
        if (rethrow) throw error;
    }
};

export const creators = {
    get,
};
